import gsap from 'gsap';
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin';

gsap.registerPlugin(ScrambleTextPlugin);

let masterTl = null;
let idleGlitchDc = null;
let sliceLoopTween = null;

function syncDataText(line) {
  if (line) line.setAttribute('data-text', line.textContent || '');
}

function startSliceLoop(gameTitle, lines) {
  sliceLoopTween?.kill();
  const tick = () => {
    const sliceY = gsap.utils.random(0, 85);
    const sliceH = gsap.utils.random(2, 14);
    const sliceX = gsap.utils.random(-12, 12);
    gsap.set(gameTitle, {
      '--slice-y': `${sliceY}%`,
      '--slice-h': `${sliceH}%`,
      '--slice-x': `${sliceX}px`,
    });
    for (const l of lines) syncDataText(l);
  };
  sliceLoopTween = gsap.to({}, {
    duration: 0.06,
    repeat: -1,
    ease: 'none',
    onRepeat: tick,
    onStart: tick,
  });
}

function stopSliceLoop(gameTitle) {
  sliceLoopTween?.kill();
  sliceLoopTween = null;
  if (gameTitle) {
    gsap.set(gameTitle, {
      '--slice-y': '0%',
      '--slice-h': '0%',
      '--slice-x': '0px',
    });
  }
}

function chromaticBurst(gameTitle, intensity = 1) {
  if (!gameTitle) return null;
  const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });
  const steps = 6;
  for (let i = 0; i < steps; i++) {
    tl.to(gameTitle, {
      '--gx': `${gsap.utils.random(-8, 8) * intensity}px`,
      '--gy': `${gsap.utils.random(-3, 3) * intensity}px`,
      x: gsap.utils.random(-6, 6) * intensity,
      y: gsap.utils.random(-3, 3) * intensity,
      skewX: gsap.utils.random(-3, 3) * intensity,
      duration: 0.045,
      ease: 'none',
    });
  }
  tl.to(gameTitle, {
    '--gx': '0px',
    '--gy': '0px',
    x: 0,
    y: 0,
    skewX: 0,
    duration: 0.12,
    ease: 'power2.out',
  });
  return tl;
}

function glitchBurst(gameTitle, lines, audio = null) {
  if (!gameTitle) return null;
  audio?.play?.('titleGlitch', { pitchJitter: 0.02 });
  gameTitle.classList.add('is-glitching');
  startSliceLoop(gameTitle, lines);
  const tl = chromaticBurst(gameTitle, 1);
  tl.eventCallback('onComplete', () => {
    gameTitle.classList.remove('is-glitching');
    stopSliceLoop(gameTitle);
  });
  return tl;
}

function scheduleIdleGlitch(gameTitle, lines, audio = null) {
  idleGlitchDc?.kill();
  const delay = gsap.utils.random(2.6, 6.5, 0.1);
  idleGlitchDc = gsap.delayedCall(delay, () => {
    if (!gameTitle?.isConnected) return;
    const burst = glitchBurst(gameTitle, lines, audio);
    burst?.eventCallback('onComplete', () => {
      gameTitle.classList.remove('is-glitching');
      stopSliceLoop(gameTitle);
      scheduleIdleGlitch(gameTitle, lines, audio);
    });
  });
}

export function killMenuTitleIntro(menu) {
  idleGlitchDc?.kill();
  idleGlitchDc = null;
  masterTl?.kill();
  masterTl = null;
  sliceLoopTween?.kill();
  sliceLoopTween = null;

  const gameTitle = menu?.querySelector('.game-title');
  const lines = menu?.querySelectorAll('.title-line');
  if (gameTitle) {
    gsap.killTweensOf(gameTitle);
    gameTitle.classList.remove('is-glitching');
    gsap.set(gameTitle, {
      clearProps: 'transform',
      '--gx': '0px',
      '--gy': '0px',
      '--slice-y': '0%',
      '--slice-h': '0%',
      '--slice-x': '0px',
    });
  }
  if (lines?.length) {
    gsap.killTweensOf(lines);
  }
}

export function playMenuTitleIntro(menu, audio = null) {
  if (!menu) return;
  killMenuTitleIntro(menu);

  const gameTitle = menu.querySelector('.game-title');
  const lines = menu.querySelectorAll('.title-line');
  const line1 = lines[0];
  const line2 = lines[1];
  if (!gameTitle || !line1 || !line2) return;

  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  if (reduceMotion) {
    line1.textContent = 'NIHIL';
    line2.textContent = 'PROJECT';
    syncDataText(line1);
    syncDataText(line2);
    return;
  }

  line1.textContent = '';
  line2.textContent = '';
  syncDataText(line1);
  syncDataText(line2);

  audio?.play?.('titleGlitch', { pitchJitter: 0.02 });
  gameTitle.classList.add('is-glitching');
  startSliceLoop(gameTitle, lines);

  masterTl = gsap.timeline({
    onComplete: () => {
      gameTitle.classList.remove('is-glitching');
      stopSliceLoop(gameTitle);
      gsap.set(gameTitle, { x: 0, y: 0, skewX: 0, '--gx': '0px', '--gy': '0px' });
      scheduleIdleGlitch(gameTitle, lines, audio);
    },
  });

  masterTl
    .add(chromaticBurst(gameTitle, 1.4), 0)
    .to(line1, {
      duration: 1.0,
      scrambleText: {
        text: 'NIHIL',
        chars: 'upperCase',
        speed: 1.05,
      },
      onUpdate: () => syncDataText(line1),
      onComplete: () => syncDataText(line1),
    }, 0)
    .to(line2, {
      duration: 1.15,
      scrambleText: {
        text: 'PROJECT',
        chars: 'upperCase',
        speed: 0.9,
      },
      onUpdate: () => syncDataText(line2),
      onComplete: () => syncDataText(line2),
    }, 0.2)
    .add(chromaticBurst(gameTitle, 0.7), '+=0.05');
}
